import cn from "@/v2-ui/utils/utils.cn"
import Dot from "@/v2-backoffice-client/components/dot/Dot"
import { CONFIDENCE_DOT_VARIANT } from "@/v2-backoffice-client/translation/translation.constants"
import type {
  GeneratedValue,
  SeedResult,
} from "@/v2-backoffice-client/translation/addObjects/translation.addObjects.utils"

export type TranslationAddObjectsPreviewRowProps = {
  code: string
  name: string
  description: string
  generated: GeneratedValue | undefined
  seedResult: SeedResult | undefined
  onEditGenerated(
    languageCode: string,
    field: "Name" | "Description",
    text: string,
  ): void
}

// One parsed value: its source cells, seed status, and — once the AI has
// run — the editable per-language texts.
function TranslationAddObjectsPreviewRow(
  props: TranslationAddObjectsPreviewRowProps,
) {
  const { code, name, description, generated, seedResult, onEditGenerated } =
    props

  return (
    <div className="rounded-md border border-border p-2">
      <div className="flex items-center justify-between gap-2">
        <div className="break-all text-sm font-medium">
          {code || <span className="text-destructive">(no code)</span>}
        </div>
        {seedResult ? (
          <span
            className={cn(
              "shrink-0 text-xs font-medium",
              seedResult.Ok ? "text-primary" : "text-destructive",
            )}
            title={seedResult.Error ?? undefined}
          >
            {seedResult.Ok ? "Seeded" : `Failed: ${seedResult.Error}`}
          </span>
        ) : null}
      </div>
      <div className="mt-1 text-sm">{name}</div>
      {description ? (
        <div className="mt-0.5 text-xs text-muted-foreground">
          {description}
        </div>
      ) : null}

      {generated ? (
        <div className="mt-2 flex flex-col gap-2 border-t border-border pt-2">
          {generated.Languages.map((lang) => (
            <div
              key={lang.LanguageCode}
              className="flex flex-col gap-1 text-xs"
            >
              <span className="flex items-center gap-1.5">
                <Dot
                  variant={CONFIDENCE_DOT_VARIANT[lang.Confidence] ?? "muted"}
                  className="shrink-0"
                  title={`Confidence: ${lang.Confidence}`}
                />
                <span className="w-12 shrink-0 text-muted-foreground">
                  {lang.LanguageCode}
                </span>
                <input
                  value={lang.Name ?? ""}
                  onChange={(e) =>
                    onEditGenerated(lang.LanguageCode, "Name", e.target.value)
                  }
                  className="flex-1 rounded-md border border-input bg-transparent px-2 py-1 font-medium shadow-sm focus-visible:border-ring focus-visible:outline-none"
                />
              </span>
              <textarea
                value={lang.Description ?? ""}
                rows={2}
                placeholder="Description"
                onChange={(e) =>
                  onEditGenerated(
                    lang.LanguageCode,
                    "Description",
                    e.target.value,
                  )
                }
                className="ml-[3.375rem] resize-y rounded-md border border-input bg-transparent px-2 py-1 text-muted-foreground shadow-sm focus-visible:border-ring focus-visible:outline-none"
              />
            </div>
          ))}
        </div>
      ) : null}
    </div>
  )
}

export default TranslationAddObjectsPreviewRow
